// Phase 11 · Step 3: Amanda's Coordination Services Agreement, filled in automatically.
// Source: "Ghostwriter Events — Coordination Services Agreement (Template)", Oct 9, 2026 (Documents/Contract in the vault).
// The wording below is hers. The engine only fills blanks, keeps the booked package, adds or drops
// Section 6 (Highline venue) and renumbers the sections and their cross-references. It gives no legal advice.
// Shared by the browser (preview) and the server (signing and the PDF). Pure JS, no imports beyond packages.js.
import { normalizeSettings, packageById, formatRate, formatCeremonyTime, agreementGaps } from "./packages.js";

export const TEMPLATE_VERSION = "2026-10-09";

/* ───────── small formatters ───────── */

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen",
  "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
export function numberWords(n) {
  n = Math.round(Number(n));
  if (!Number.isFinite(n) || n < 0) return String(n);
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? "-" + ONES[n % 10] : "");
  if (n < 1000) return ONES[Math.floor(n / 100)] + " hundred" + (n % 100 ? " " + numberWords(n % 100) : "");
  return String(n);
}
/** 14 → "fourteen (14)", the way her contract writes numbers. Non-whole numbers stay as digits. */
export const wordsAndDigits = n => Number.isInteger(Number(n)) ? numberWords(n) + " (" + Number(n) + ")" : String(n);

export const money = n => {
  const v = Math.round(Number(n || 0) * 100) / 100;
  const a = Math.abs(v);
  return (v < 0 ? "−" : "") + "$" + a.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(a) ? 0 : 2, maximumFractionDigits: 2 });
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "Jun 12, 2027" / "2027-06-12" → a UTC date at noon, or null. */
export function parseDay(v) {
  const s = String(v || "").trim();
  let y, m, d;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) { y = +iso[1]; m = +iso[2] - 1; d = +iso[3]; }
  else {
    const t = Date.parse(s);
    if (!Number.isFinite(t)) return null;
    const x = new Date(t); y = x.getFullYear(); m = x.getMonth(); d = x.getDate();
  }
  const out = new Date(Date.UTC(y, m, d, 12));
  return Number.isFinite(out.getTime()) ? out : null;
}
export const longDate = dt => dt ? DAYS[dt.getUTCDay()] + ", " + MONTHS[dt.getUTCMonth()] + " " + dt.getUTCDate() + ", " + dt.getUTCFullYear() : "";
export const plainDate = dt => dt ? MONTHS[dt.getUTCMonth()] + " " + dt.getUTCDate() + ", " + dt.getUTCFullYear() : "";
export const addDays = (dt, n) => dt ? new Date(dt.getTime() + n * 86400000) : null;

/* ───────── money for Section 4 ───────── */

/**
 * The fee table. When the approved quote has typed lines (package · addon · custom), the agreement uses
 * exactly those lines, plus an "Adjustment" row if she set a final number that differs from their sum.
 * Otherwise: the package fee (its Settings price, or the approved quote's fee when quoted case by case)
 * plus each add-on chosen on the couple's profile. Installments split by the retainer percentage.
 */
export function agreementMoney({ client, settings, quote }) {
  const s = normalizeSettings(settings);
  const c = client || {};
  const pkg = c.packageId ? packageById(s, c.packageId) : null;
  const lines = quote && Array.isArray(quote.lineItems) ? quote.lineItems.filter(li => li && li.kind) : [];
  const pkgLine = lines.find(li => li.kind === "package");
  let base = null;
  let addons;
  if (pkgLine && Number(pkgLine.amount) > 0) {
    base = Number(pkgLine.amount);
    addons = lines.filter(li => (li.kind === "addon" || li.kind === "custom") && Number(li.amount) > 0)
      .map((li, i) => ({ id: li.addonId || "line-" + i, label: String(li.label || "").trim() || "Additional service", amount: Number(li.amount) }));
    const sum = base + addons.reduce((t, a) => t + a.amount, 0);
    const fin = Number(quote.finalNumber);
    if (fin > 0 && Math.abs(fin - sum) >= 0.005) addons.push({ id: "adjustment", label: "Adjustment", amount: Math.round((fin - sum) * 100) / 100 });
  } else {
    if (pkg && pkg.price != null) base = pkg.price;
    else if (quote && Number(quote.finalNumber) > 0) base = Number(quote.finalNumber);
    const qty = c.addonQty || {};
    addons = (c.addonIds || []).map(id => s.addons.find(a => a.id === id)).filter(Boolean).map(a => {
      const n = a.unit === "flat" ? 1 : Math.max(1, Number(qty[a.id]) || 1);
      return { id: a.id, label: a.name + (a.unit === "flat" ? "" : " (" + n + " " + (a.unit === "hour" ? (n === 1 ? "hour" : "hours") : (n === 1 ? "person" : "people")) + ")"), amount: a.rate * n };
    });
  }
  const total = base == null ? null : Math.round((base + addons.reduce((t, a) => t + a.amount, 0)) * 100) / 100;
  const retainer = total == null ? null : Math.round(total * s.terms.retainerPct) / 100;
  const balance = total == null ? null : Math.round((total - retainer) * 100) / 100;
  const event = parseDay(c.date);
  return { pkg, base, addons, total, retainer, balance, balanceDue: addDays(event, -s.terms.balanceDaysBefore), event };
}

/* ───────── the template ─────────
   {{key}}     → a value from fill()
   {{n:key}}   → this section's number;  {{s:key}} → a reference to another section's number
   [[if highline]] … [[/if]] → kept only for a venue operated by HLADV LLC                     */

const TEMPLATE = `# Wedding Coordination Services Agreement

*{{legalName}} dba {{dba}} · {{homeBase}}*

This Wedding Coordination Services Agreement (the “Agreement”) is entered into by and between {{legalName}}, {{entityType}} doing business as {{dba}} (“Coordinator”), and {{client1}} and {{client2}} (together, “Client”). Coordinator and Client are each a “Party” and together the “Parties.” This Agreement is effective on the date it is signed by the last Party to sign (the “Effective Date”).

## {{n:event}}. Event Details

| Item | Detail |
| :---- | :---- |
| Client | {{client1}} and {{client2}} |
| Client Email | {{clientEmail}} |
| Client Phone | {{clientPhone}} |
| Event Date | {{eventDate}} |
| Ceremony Start Time | {{ceremonyTime}} |
| Venue | {{venue}} |
| Estimated Guest Count | {{guests}} |

Client shall notify Coordinator in writing of any change to the event date, start time, venue, or guest count as soon as the change is known. Changes to the event date are governed by Section {{s:cancellation}}.

## {{n:services}}. Services

{{services}}

**On-site hours (all packages).** On-site hours beyond that stated above will be billed at {{overtimeRate}} per hour, in {{overtimeIncrement}} increments, invoiced after the Event Date and payable within {{invoiceDueDays}} days. Coordinator will not remain on site beyond {{maxOnSiteHours}} hours without Client’s approval of the additional charge.

## {{n:excluded}}. Services Not Included

The following are outside the scope of this Agreement and are available only by separate written agreement or as an add-on under Section {{s:addons}}:

- Design, styling, floral arrangement, or décor creation, except design direction included in the Full-Service package
- Budget creation or budget management, except as included in the Full-Service package
- Vendor sourcing under the Wedding Day Coordination package (available as an add-on)
- Legal review of, or negotiation of, any vendor contract. Coordinator’s contract review is limited to logistics (timing, load-in, headcount, and deliverables) and is not legal advice
- Signing contracts, making payments, or incurring obligations on Client’s behalf
- Physical setup, teardown, hauling, or transport of décor, furniture, or rentals. Coordinator oversees these activities but does not perform them
- Personal errands, guest transportation, childcare, or pet care
- Guest lodging coordination or vow and toast writing help, unless added under Section {{s:addons}}
- Officiating, catering, bartending, or any licensed vendor service

## {{n:fees}}. Fees, Retainer, and Payment

{{feeTable}}

The Total Fee is payable in two installments:

| Installment | Amount | Due |
| :---- | :---- | :---- |
| Retainer ({{retainerPct}}%) | {{retainerAmount}} | Within {{retainerDueDays}} days of the Effective Date{{retainerDate}} |
| Balance ({{balancePct}}%) | {{balanceAmount}} | On or before {{balanceDate}} ({{balanceDaysBefore}} days before the Event Date) |

Each installment is due on the date stated above, whether or not Client has received an invoice. Coordinator will send invoices as a courtesy.

**Retainer.** The Retainer reserves the Event Date on Coordinator’s calendar and compensates Coordinator for declining other bookings for that date. The Retainer is earned upon receipt and is non-refundable, except as stated in Section {{s:cancellation}} (Cancellation by Coordinator) and Section {{s:substitution}}. The date is not reserved, and Coordinator has no obligation to perform, until the Retainer is received.

**Late payment.** If any payment is more than {{latePauseDays}} days overdue, Coordinator may suspend all services until the account is current. Coordinator is not responsible for any deadline, vendor arrangement, or detail missed during a suspension.

## {{n:addons}}. Add-On Services

Add-ons included as of the Effective Date: {{addonsIncluded}}. Client may add any of the following at the rates below by written request (email is sufficient). Add-ons requested within {{addonLateRequestDays}} days of the Event Date are subject to Coordinator’s availability. Add-ons are invoiced separately and payable within {{invoiceDueDays}} days of the invoice date.

{{addonTable}}

These rates are locked for this Agreement and will not increase before the Event Date, including for one postponement under Section {{s:cancellation}}.

[[if highline]]
## {{n:venue}}. Venue Relationship Disclosure

Client acknowledges that the principal of {{legalName}} is also affiliated with Highline Adventures (HLADV LLC), the operator of the Venue. Coordination services under this Agreement are provided by {{legalName}} dba {{dba}} and are separate from, and not a substitute for, Client’s venue rental agreement with HLADV LLC. Each agreement is governed by its own terms, and payment or default under one does not affect the other. Client may engage any coordinator of its choosing, and Client’s selection of Coordinator is not a condition of the venue rental.
[[/if]]

## {{n:responsibilities}}. Client Responsibilities

- Client makes all final decisions regarding vendors, budget, design, and event schedule. Coordinator advises and implements; Coordinator does not decide
- Client will provide Coordinator with copies of all vendor contracts, the guest count, and all relevant contact information no later than sixty (60) days before the Event Date
- Client is responsible for all payments owed directly to vendors and to the Venue
- Client is responsible for securing any event insurance required by the Venue and for the conduct of its guests
- Client will designate one person authorized to make day-of decisions if Client is unavailable
- Client is responsible for including vendor meals in the final catering order, covering the photographer, DJ, Coordinator’s assistants, and any other staff working the event from start to finish. Before Client’s final catering order is due, Coordinator will provide Client with a plate count of all expected vendors requiring a meal
- Client will respond to Coordinator’s requests for information in a timely manner. Coordinator is not responsible for outcomes caused by information provided late or not at all

## {{n:travel}}. Travel

Services performed within {{region}} are included in the Total Fee. If the event is held or relocated outside {{region}}, travel, mileage, and lodging costs will be quoted in advance and invoiced separately, and Coordinator may decline a relocation that is not feasible.

## {{n:subcontractors}}. Subcontractors

Coordinator may engage qualified subcontractors, including assistant coordinators, setup/breakdown crew, and writers, to perform part of the services. Coordinator remains responsible for all services under this Agreement and pays its subcontractors directly; Client owes nothing to them. Each subcontractor is bound by the same confidentiality and conduct standards as Coordinator. Any vow, toast, or speech drafts prepared for Client are confidential, are shared only with those working on them, and belong to Client once paid for.

## {{n:cancellation}}. Cancellation and Postponement

**Cancellation by Client.** Client may cancel at any time by written notice. The Retainer is non-refundable. If Client cancels more than {{cancelDays}} days before the Event Date, no further amount is owed. If Client cancels {{cancelDays}} days or fewer before the Event Date, the full Total Fee is due, as Coordinator has by then performed the substantial portion of the pre-event work and has held the date against other bookings.

**Postponement.** Client may postpone once, without additional fee, to a new date within {{postponeMonths}} months of the original Event Date, subject to Coordinator’s availability. Amounts paid are applied to the new date, and the Total Fee and add-on rates in this Agreement stay the same. Any further postponement is subject to Coordinator’s rates in effect at that time. If Coordinator is unavailable on the new date, the postponement is treated as a cancellation as of the date notice was given.

**Cancellation by Coordinator.** If Coordinator cancels for any reason other than Client’s breach, Coordinator will refund all amounts paid, including the Retainer, and will use reasonable efforts to identify a qualified replacement coordinator. This refund is Client’s sole remedy.

## {{n:substitution}}. Substitution and Force Majeure

If Coordinator is unable to attend the Event due to illness, injury, emergency, or other cause beyond Coordinator’s control, Coordinator will use commercially reasonable efforts to provide a qualified substitute coordinator at no additional cost to Client, and will deliver the completed timeline and all vendor information to that substitute. If no substitute can be secured, Coordinator’s liability is limited to a refund of amounts paid.

Neither Party is liable for failure to perform due to events beyond its reasonable control, including natural disaster, fire, flood, wildfire, evacuation order, public health emergency, government restriction, or utility failure. In such an event the Parties will work in good faith toward a postponement under Section {{s:cancellation}}.

## {{n:liability}}. Third-Party Vendors and Limitation of Liability

Coordinator is not a party to any agreement between Client and any vendor or the Venue, and is not responsible for the acts, omissions, delays, defects, non-appearance, or performance of any third-party vendor, the Venue, or any guest. Coordinator does not guarantee vendor availability or pricing.

Coordinator’s total aggregate liability arising out of or relating to this Agreement, whether in contract, tort, or otherwise, shall not exceed the total amount actually paid by Client to Coordinator under this Agreement. Neither Party is liable for indirect, incidental, consequential, punitive, or special damages, including lost profits or emotional distress.

## {{n:general}}. General Provisions

**Independent Contractor.** Coordinator is an independent contractor. Nothing in this Agreement creates an employment, partnership, agency, or joint venture relationship.

**Photography and Promotion.** Coordinator may photograph the event setup and use images of the event, without identifying Client by name, for portfolio and marketing purposes. Client may opt out by written notice before the Event Date.

**Conduct.** Coordinator may leave the premises and suspend services without refund if Coordinator or any member of Coordinator’s team is subjected to abusive, threatening, or unsafe conduct by Client, a guest, or a vendor.

**Notices.** Written notice under this Agreement may be given by email to the addresses in Section {{s:event}} and in the signature block, and is effective on the date sent.

**Governing Law and Disputes.** This Agreement is governed by the laws of the State of California. The Parties will attempt in good faith to resolve any dispute through mediation in Santa Barbara County before pursuing litigation. Venue for any action lies in Santa Barbara County, California. The prevailing Party is entitled to recover reasonable attorneys’ fees and costs.

**Entire Agreement.** This Agreement is the complete agreement between the Parties regarding coordination services and supersedes all prior discussions, quotes, and proposals. It may be amended only in a writing signed by both Parties, except that add-on services may be added by written request under Section {{s:addons}}. If any provision is held unenforceable, the remainder stays in effect. Electronic signatures and counterparts are valid and binding.

## Agreed and Accepted

**COORDINATOR — {{legalNameUpper}} dba {{dbaUpper}}**
Signature, print name and title, and date: recorded electronically below.

**CLIENT — {{client1Upper}}**
Signature and date: recorded electronically below. Email: {{email1}}

**CLIENT — {{client2Upper}}**
Signature and date: recorded electronically below. Email: {{email2}}
`;

/* ───────── Section 2: the booked package ───────── */

function servicesText(pkg, s) {
  const hours = "Up to " + wordsAndDigits(pkg.onSiteHours) + " hours on site on the Event Date";
  let items = pkg.inclusions.slice();
  const base = pkg.includes ? packageById(s, pkg.includes) : null;
  if (base) {
    // Her template says "everything in Option B, plus:". Option B isn't in a filled agreement,
    // so its list is written out first, then this package's additions (repeats dropped).
    const extra = items.filter(i => !base.inclusions.includes(i));
    items = base.inclusions.concat(extra);
  }
  const starts = String(pkg.involvementStarts || "").trim();
  const period = /^(the |on )/i.test(starts) || /\d{4}/.test(starts);
  let intro;
  if (pkg.id === "wedding-day") {
    intro = "Coordinator will provide the " + pkg.name + " package for a Client who has already booked their vendors. Coordinator’s active involvement begins " + starts + " and includes:";
  } else if (period) {
    intro = "Coordinator will provide the " + pkg.name + " package. Coordinator’s active involvement begins on " + starts.replace(/^on /i, "") + " (the “Planning Period”) and includes:";
  } else {
    intro = "Coordinator will provide the " + pkg.name + " package. Coordinator’s active involvement begins " + starts + " and includes:";
  }
  return "**" + pkg.name + ".** " + intro + "\n\n" + items.concat(hours).map(i => "- " + i).join("\n");
}

/* ───────── fill ───────── */

const SECTION_ORDER = ["event", "services", "excluded", "fees", "addons", "venue", "responsibilities", "travel", "subcontractors", "cancellation", "substitution", "liability", "general"];

/**
 * Fills the agreement for one couple.
 * Returns { ok, body, missing, version, money }. When something is missing, ok is false and `missing`
 * lists it in plain words ("Ceremony start time"); the body is not produced, so nothing half-filled is ever sent.
 */
export function fillContract({ client, settings, quote, effectiveDate } = {}) {
  const s = normalizeSettings(settings);
  const c = client || {};
  const missing = agreementGaps(c, s);
  const m = agreementMoney({ client: c, settings: s, quote });
  if (m.pkg && m.base == null) missing.push("The package fee (set it in Settings, or approve a quote)");
  if (missing.length) return { ok: false, missing, version: TEMPLATE_VERSION, money: m };

  const t = s.terms;
  const p = c.partners || [];
  const emails = p.map(x => x && x.email).filter(Boolean);
  const phones = p.map(x => x && x.phone).filter(Boolean);
  const eff = effectiveDate ? parseDay(effectiveDate) : null;

  const feeRows = [["**" + m.pkg.name + "**", money(m.base)]].concat(m.addons.map(a => [a.label, money(a.amount)]));
  const feeTable = "| Item | Amount |\n| :---- | :---- |\n" + feeRows.map(r => "| " + r[0] + " | " + r[1] + " |").join("\n") + "\n| **Total Fee** | **" + money(m.total) + "** |";
  const addonTable = "| Service | Rate |\n| :---- | :---- |\n" + s.addons.map(a => "| " + a.name + (a.note ? " (" + a.note.charAt(0).toLowerCase() + a.note.slice(1) + ")" : "") + " | " + formatRate(a) + " |").join("\n");

  const v = {
    legalName: s.legalName, entityType: s.entityType, dba: s.dba, role: s.role, homeBase: s.homeBase, region: s.region,
    legalNameUpper: s.legalName.toUpperCase(), dbaUpper: s.dba.toUpperCase(), roleUpper: s.role.toUpperCase(),
    client1: p[0].name, client2: p[1].name, client1Upper: p[0].name.toUpperCase(), client2Upper: p[1].name.toUpperCase(),
    email1: (p[0] && p[0].email) || "—", email2: (p[1] && p[1].email) || "—",
    clientEmail: emails.join(" · "), clientPhone: phones.join(" · "),
    eventDate: longDate(m.event), ceremonyTime: formatCeremonyTime(c.ceremonyTime),
    venue: c.venue + ", " + c.venueAddress, guests: String(c.guests),
    services: servicesText(m.pkg, s),
    overtimeRate: money(t.overtimeRate),
    overtimeIncrement: t.overtimeIncrementHrs === 0.5 ? "half-hour" : t.overtimeIncrementHrs === 1 ? "one-hour" : t.overtimeIncrementHrs + "-hour",
    invoiceDueDays: wordsAndDigits(t.invoiceDueDays), maxOnSiteHours: wordsAndDigits(t.maxOnSiteHours),
    feeTable, addonTable,
    retainerPct: String(t.retainerPct), balancePct: String(100 - t.retainerPct),
    retainerAmount: money(m.retainer), balanceAmount: money(m.balance),
    retainerDueDays: wordsAndDigits(t.retainerDueDays), retainerDate: eff ? " (" + plainDate(addDays(eff, t.retainerDueDays)) + ")" : "",
    balanceDate: plainDate(m.balanceDue), balanceDaysBefore: String(t.balanceDaysBefore),
    latePauseDays: wordsAndDigits(t.latePauseDays),
    addonsIncluded: m.addons.length ? m.addons.map(a => a.label).join("; ") : "None",
    addonLateRequestDays: wordsAndDigits(t.addonLateRequestDays),
    cancelDays: wordsAndDigits(t.cancelFullFeeWithinDays), postponeMonths: wordsAndDigits(t.postponeWithinMonths)
  };

  let body = TEMPLATE.replace(/\[\[if highline\]\]\n?([\s\S]*?)\[\[\/if\]\]\n?/g, (_, inner) => c.highlineVenue ? inner : "");
  const numbers = {};
  let n = 0;
  SECTION_ORDER.forEach(k => { if (body.includes("{{n:" + k + "}}")) numbers[k] = ++n; });
  body = body.replace(/\{\{([ns]):([a-z]+)\}\}/g, (all, _kind, k) => numbers[k] ? String(numbers[k]) : all);
  body = body.replace(/\{\{([A-Za-z0-9]+)\}\}/g, (all, k) => (v[k] == null ? all : String(v[k])));
  body = body.replace(/\n{3,}/g, "\n\n").trim() + "\n";

  const leftover = unfilled(body);
  if (leftover.length) return { ok: false, missing: leftover.map(x => "Template blank " + x), version: TEMPLATE_VERSION, money: m };
  return { ok: true, body, missing: [], version: TEMPLATE_VERSION, money: m, sections: numbers };
}

/** Any blank the template didn't fill: {{placeholder}}, [[block]] or a [bracket note]. Empty array = clean. */
export function unfilled(body) {
  const out = [];
  String(body || "").replace(/\{\{[^}]*\}\}|\[\[[^\]]*\]\]|\[[^\]\n]*\]/g, x => { out.push(x); return x; });
  return out;
}
